import React, { useState } from 'react';

import { useSnackbar } from 'notistack';
import fileDownload from 'react-file-download';

import { Box, Button, Stack, TextField, Typography } from '@mui/material';

import { downloadGHARawReportCSV } from '~/services/ConnectionGoogleService';

function GHARawReportDownload() {
  const { enqueueSnackbar } = useSnackbar();
  const [propertyIdsInput, setPropertyIdsInput] = useState('');
  const [createdAtStart, setCreatedAtStart] = useState('');
  const [createdAtEnd, setCreatedAtEnd] = useState('');
  const [limit, setLimit] = useState('100');
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownload = async () => {
    if (isDownloading) {
      return;
    }

    const trimmedLimit = limit.trim();
    if (trimmedLimit && Number.isNaN(Number(trimmedLimit))) {
      enqueueSnackbar('Limit must be a number', { variant: 'warning' });
      return;
    }

    if (trimmedLimit && Number(trimmedLimit) <= 0) {
      enqueueSnackbar('Limit must be greater than zero', { variant: 'warning' });
      return;
    }

    const parseDateValue = (value: string, isEndOfDay = false) => {
      if (!value) {
        return undefined;
      }

      const date = new Date(`${value}T00:00:00.000Z`);
      if (Number.isNaN(date.getTime())) {
        return undefined;
      }

      if (isEndOfDay) {
        date.setUTCHours(23, 59, 59, 999);
      }

      return date;
    };

    const parsedCreatedAtStart = parseDateValue(createdAtStart);
    if (createdAtStart && !parsedCreatedAtStart) {
      enqueueSnackbar('Please provide a valid start date (UTC)', { variant: 'warning' });
      return;
    }

    const parsedCreatedAtEnd = parseDateValue(createdAtEnd, true);
    if (createdAtEnd && !parsedCreatedAtEnd) {
      enqueueSnackbar('Please provide a valid end date (UTC)', { variant: 'warning' });
      return;
    }

    if (parsedCreatedAtStart && parsedCreatedAtEnd && parsedCreatedAtStart > parsedCreatedAtEnd) {
      enqueueSnackbar('Start date must be before or equal to the end date', { variant: 'warning' });
      return;
    }

    const propertyIds = propertyIdsInput
      .split(',')
      .map((id) => id.trim())
      .filter(Boolean);

    setIsDownloading(true);

    try {
      const blob = await downloadGHARawReportCSV({
        propertyIds: propertyIds.length > 0 ? propertyIds : undefined,
        createdAtStart: parsedCreatedAtStart ? parsedCreatedAtStart.toISOString() : undefined,
        createdAtEnd: parsedCreatedAtEnd ? parsedCreatedAtEnd.toISOString() : undefined,
        limit: trimmedLimit || undefined,
      });

      fileDownload(blob, 'gha-raw-report.csv');

      enqueueSnackbar('Raw report CSV downloaded successfully', { variant: 'success' });
    } catch (error) {
      if (error instanceof Error) {
        enqueueSnackbar(error.message, { variant: 'error' });
      } else if (typeof error === 'object' && error !== null && 'message' in error) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        enqueueSnackbar((error as { message: string }).message, { variant: 'error' });
      } else {
        enqueueSnackbar('Failed to download raw report CSV', { variant: 'error' });
      }
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Box>
      <Typography variant="h6" component="h2" gutterBottom>
        Google Hotel Ads Raw Report
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Negative values in the le_margin or le_price columns mean we could not capture the price at that moment.
      </Typography>
      <Stack spacing={2}>
        <TextField
          label="Property IDs"
          placeholder="12345,67890"
          value={propertyIdsInput}
          onChange={(event) => setPropertyIdsInput(event.target.value)}
          helperText="Comma-separated property IDs (optional)."
          fullWidth
          multiline
          minRows={2}
        />
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <TextField
            label="Created At Start (UTC)"
            type="date"
            value={createdAtStart}
            onChange={(event) => setCreatedAtStart(event.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
          <TextField
            label="Created At End (UTC)"
            type="date"
            value={createdAtEnd}
            onChange={(event) => setCreatedAtEnd(event.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
          <TextField
            label="Limit"
            type="number"
            value={limit}
            onChange={(event) => setLimit(event.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
        </Stack>
        <Box>
          <Button variant="contained" onClick={handleDownload} disabled={isDownloading}>
            {isDownloading ? 'Downloading…' : 'Download CSV'}
          </Button>
        </Box>
      </Stack>
    </Box>
  );
}

export default GHARawReportDownload;
